import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'
import {
  WizardContent,
  WizardHeading,
  WizardTemplate,
  WizardTitle,
} from '~/templates/wizard'

import { Button, Heading1, Icon } from '@hap/components'

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/scorecard/new/template',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath })
  const trpc = useTRPC()

  const { data: templates, isLoading } = useQuery(
    trpc.scorecards.listTemplates.queryOptions(),
  )

  const { createFromTemplate, isPending } = ScorecardMutators.useCreateFromTemplate({
    onSuccess: (scorecardId) => {
      navigate({
        to: '/$alias/scorecard/$scorecardId/edit',
        params: { scorecardId },
      })
    },
  })

  const handleSelect = useCallback(
    async (templateId: string) => {
      await createFromTemplate(templateId)
    },
    [createFromTemplate],
  )

  return (
    <WizardTemplate title="Select Template">
      <WizardHeading>
        <WizardTitle
          breadcrumbs={[
            {
              to: '/$alias/scorecards',
              from: Route.fullPath,
              icon: 'sidebar',
              children: 'Scorecards',
            },
            {
              to: '/$alias/scorecard/new',
              from: Route.fullPath,
              children: 'New Scorecard',
            },
            { children: 'Select Template' },
          ]}
        />
      </WizardHeading>
      <WizardContent>
        <div className="flex w-full max-w-5xl flex-col gap-10 px-6">
          <header className="mb-8">
            <Heading1>Select a Template</Heading1>
            <p>Choose a pre-built scorecard to get started.</p>
          </header>

          {isLoading ? (
            <div className="flex flex-col items-center gap-4 py-12">
              <Icon name="arrows-clockwise" className="size-8 animate-spin" />
              <p className="text-muted-foreground text-sm">Loading templates...</p>
            </div>
          ) : !templates?.length ? (
            <div className="text-muted-foreground py-12 text-center">
              No templates available.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  name={template.name}
                  onClick={() => handleSelect(template.id)}
                  disabled={isPending}
                />
              ))}
            </div>
          )}

          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() =>
              navigate({ to: '/$alias/scorecard/new', from: Route.fullPath })
            }
            disabled={isPending}
          >
            Back
          </Button>
        </div>
      </WizardContent>
    </WizardTemplate>
  )
}

function TemplateCard(props: { name: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={props.onClick}
      disabled={props.disabled}
      className="bg-background hover:bg-accent flex flex-col items-start gap-3 rounded-lg border p-6 text-left transition-colors disabled:opacity-50"
    >
      <div className="bg-lime-green border-foreground flex size-12 items-center justify-center rounded-md">
        <Icon name="copy" className="size-6" />
      </div>
      <div className="font-medium">{props.name}</div>
    </button>
  )
}
